import React, { useState } from 'react';
import { Order } from '@hero/schema';
import { useTranslation } from 'react-i18next';
import { formatDateMain } from '@hero/core';
import { Button } from '@hero/web-components';
import { PlusIcon } from '@hero/web-icons';
import { OrderImagesEmpty } from './OrderPhotosEmpty';
import { OrderAddImagesModal } from '../Photo/OrderAddImagesModal';

export interface OrderPhotosTabProps {
  order: Order;
  /** When true, shows the Add image trigger (carrier with edit permission). */
  canAdd?: boolean;
}

export const OrderPhotosTab: React.FC<OrderPhotosTabProps> = ({ order, canAdd = false }) => {
  const { t } = useTranslation('m-order');
  const [isAddOpen, setIsAddOpen] = useState(false);
  const images = order.images || [];

  return (
    <div className="flex flex-col">
      {canAdd && images.length > 0 && (
        <div className="flex justify-end px-4 pt-4">
          <Button type="button" variant="outline" size="sm" onClick={() => setIsAddOpen(true)}>
            <PlusIcon className="size-4" />
            {t('drawer.photos_add')}
          </Button>
        </div>
      )}

      {!images.length ? (
        <OrderImagesEmpty canAdd={canAdd} onAdd={() => setIsAddOpen(true)} />
      ) : (
        <div className="overflow-y-auto max-h-[calc(100vh-200px)] grid grid-cols-2 gap-4 p-4">
          {images.map((orderImage, index) => {
            const dateLabel = formatDateMain({
              date: orderImage.date,
              format: 'date_time',
            });
            const typeLabel = t(`image_types.${orderImage.type}`);

            return (
              <a
                key={index}
                href={orderImage.image.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group block overflow-hidden border rounded-lg">
                <div className="relative aspect-square">
                  <img
                    src={orderImage.image.url}
                    alt={`${typeLabel} - ${dateLabel}`}
                    className="object-cover w-full h-full transition-transform duration-200 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-200" />
                </div>
                <div className="px-3 py-2 bg-white border-t">
                  <p className="text-sm font-medium text-gray-800 truncate">{typeLabel}</p>
                  {orderImage.customRef && (
                    <p className="text-xs text-gray-500 mt-0.5 truncate">
                      {orderImage.type === 'proof_of_delivery_doc'
                        ? t('form.pod_ref.title')
                        : t('form.document_ref.title')}
                      : {orderImage.customRef}
                    </p>
                  )}
                  {dateLabel && (
                    <p className="text-xs text-gray-500 mt-0.5">{dateLabel}</p>
                  )}
                </div>
              </a>
            );
          })}
        </div>
      )}

      {canAdd && (
        <OrderAddImagesModal
          order={order}
          isOpen={isAddOpen}
          onClose={() => setIsAddOpen(false)}
        />
      )}
    </div>
  );
};
